import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import moment from "moment";
import * as XLSX from 'xlsx';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';

import { InventoryService, InventoryFilters } from '../services/inventory/inventory.service';
import { ToasterService } from '@ui-nuvertos/auth';
import {
  DynamicListingComponent,
  FilterConfig,
  ColumnConfig,
  ActionConfig,
  ActionEvent,
  FilterEvent,
} from '../dynamic-listing/dynamic-listing';
import { SVG_ICONS } from '../utils/svg-icons.utils';

@Component({
  selector: 'lib-inventory-list',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule,
    DynamicListingComponent,
    ButtonModule,
    CheckboxModule,
    TooltipModule
  ],
  templateUrl: './inventory-list.html',
  styleUrls: ['./inventory-list.css'],
})
export class InventoryList implements OnInit {
  isLoading = false;
  exportFilename = `inventory_${new Date().toISOString().slice(0, 10)}`;

  tableData: any[] = [];
  filterValues: { [key: string]: any } = {};

  filterConfigs: FilterConfig[] = [];

  columnConfigs: ColumnConfig[] = [
    { key: 'item_number', header: 'Item No.' },
    { key: 'item_name', header: 'Item Name', ellipsis: true },
    { key: 'description', header: 'Description', ellipsis: true },
    { key: 'location', header: 'Location' },
    { key: 'salt', header: 'Salt', ellipsis: true },
    { key: 'type', header: 'Type' },
    { key: 'unit', header: 'Unit' },
    { key: 'packing_unit', header: 'Packing Unit' },
    { key: 'sku', header: 'SKU' },
    { key: 'manufacturer', header: 'Manufacturer', ellipsis: true },
    { key: 'current_stock', header: 'Current Stock', type: 'number', align: 'right' },
    { key: 'minimum_stock', header: 'Minimum Stock Level', type: 'number', align: 'right' },
    { key: 'maximum_stock', header: 'Maximum Stock Level', type: 'number', align: 'right' },
    { key: 'rack_no', header: 'Rack No.' },
  ];

  actionConfigs: ActionConfig[] = [
    { key: 'view', label: 'View', tooltip: 'View inventory', severity: 'secondary', svgIcon: SVG_ICONS.VIEW },
  ];

  pageIndex = 1;
  rowsPerPage = 10;
  totalCount = 0;
  totalPages = 0;

  // Summary totals
  totalMRP = 0;
  totalRate = 0;

  // Filter checkboxes
  showLowStock = false;
  showNearExpiry = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private inventoryService: InventoryService,
    private toasterService: ToasterService
  ) {}

  async ngOnInit() {
    this.initializeFilterConfigs();
    await this.loadInventories();
  }

  initializeFilterConfigs() {
    this.filterConfigs = [
      {
        key: 'item_number',
        type: 'text',
        placeholder: 'Item No.',
        value: this.filterValues['item_number'],
        showInAccordion: true,
      },
      {
        key: 'item_name',
        type: 'text',
        placeholder: 'Item Name',
        value: this.filterValues['item_name'],
        showInAccordion: true,
      },
      {
        key: 'location',
        type: 'text',
        placeholder: 'Location',
        value: this.filterValues['location'],
        showInAccordion: true,
      },
      {
        key: 'unit',
        type: 'text',
        placeholder: 'Unit',
        value: this.filterValues['unit'],
        showInAccordion: true,
      },
      {
        key: 'manufacturer',
        type: 'text',
        placeholder: 'Manufacturer',
        value: this.filterValues['manufacturer'],
        showInAccordion: true,
      },
    ];
  }

  async loadInventories(page: number = 1) {
    try {
      this.isLoading = true;
      this.pageIndex = page;

      const filters: InventoryFilters = {
        limit: this.rowsPerPage,
        offset: (this.pageIndex - 1) * this.rowsPerPage,
        is_deleted: false,
      };

      // Apply text filters
      if (this.filterValues['item_number']) {
        filters.serial_number = this.filterValues['item_number'];
      }
      
      if (this.filterValues['item_name']) {
        filters.item_name = this.filterValues['item_name'];
      }

      // Apply checkbox filters
      if (this.showLowStock) {
        // This would need backend support - for now we'll filter client-side
      }

      if (this.showNearExpiry) {
        // This would need backend support - for now we'll filter client-side
      }

      const response = await lastValueFrom(this.inventoryService.searchInventories(filters));
      const inventories = response?.data?.inventories ?? [];

      // Transform inventory data to match table structure
      this.tableData = inventories.map((inventory: any, index: number) => {
        // Calculate totals
        const mrp = inventory.mrp || 0;
        const rate = inventory.rate || 0;
        const quantity = inventory.current_quantity || 0;
        
        this.totalMRP += mrp * quantity;
        this.totalRate += rate * quantity;

        return {
          id: inventory.id || '',
          item_number: inventory.serial_number || inventory.item_id || '',
          item_name: inventory.item_master_name || '',
          description: inventory.item_master_description || '',
          location: inventory.location_name || inventory.location || 'N/A',
          salt: inventory.composition || 'N/A',
          type: inventory.item_type_name || 'N/A',
          unit: inventory.packaging_name || 'N/A',
          packing_unit: inventory.packing_unit || inventory.pack || 'N/A',
          sku: inventory.sku || 'N/A',
          manufacturer: inventory.manufacturer_name || 'N/A',
          current_stock: inventory.current_quantity || 0,
          minimum_stock: inventory.minimum_quantity || 'N/A',
          maximum_stock: inventory.maximum_quantity || 'N/A',
          rack_no: inventory.rack_number || 'N/A',
          mrp: mrp,
          rate: rate,
        };
      });

      // Reset totals before calculating
      this.totalMRP = 0;
      this.totalRate = 0;
      
      // Recalculate totals
      inventories.forEach((inventory: any) => {
        const mrp = inventory.mrp || 0;
        const rate = inventory.rate || 0;
        const quantity = inventory.current_quantity || 0;
        this.totalMRP += mrp * quantity;
        this.totalRate += rate * quantity;
      });

      this.totalCount = response?.data?.total ?? this.tableData.length;
      this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.rowsPerPage));
    } catch (error) {
      console.error('Error loading inventories:', error);
      this.tableData = [];
      this.totalCount = 0;
      this.totalPages = 0;
      this.totalMRP = 0;
      this.totalRate = 0;
      this.toasterService.error('Error', 'Failed to load inventory items');
    } finally {
      this.isLoading = false;
    }
  }

  onFilterEvent(event: FilterEvent) {
    this.filterValues = event.data ?? {};
    this.pageIndex = 1;
    this.loadInventories(this.pageIndex);
  }

  onColumnsSelected(_: string[]) {
    // Future: save column preferences
  }

  onActionClicked(event: ActionEvent) {
    const inventoryId = event.row?.id;
    if (!inventoryId) return;

    switch (event.action) {
      case 'view':
        this.view(inventoryId);
        break;
    }
  }

  onPageChanged(page: number) {
    this.loadInventories(page);
  }

  onShowLowStockChange(event: any) {
    this.showLowStock = event.checked;
    this.pageIndex = 1;
    this.loadInventories(this.pageIndex);
  }

  onShowNearExpiryChange(event: any) {
    this.showNearExpiry = event.checked;
    this.pageIndex = 1;
    this.loadInventories(this.pageIndex);
  }

  // Export functions
  exportCsv() {
    if (!this.tableData.length) {
      this.toasterService.info('Info', 'No data available to export.');
      return;
    }

    const headers = [
      'S.No.', 'Item No.', 'Item Name', 'Description', 'Location', 'Salt', 'Type', 
      'Unit', 'Packing Unit', 'SKU', 'Manufacturer', 'Current Stock', 
      'Minimum Stock Level', 'Maximum Stock Level', 'Rack No.'
    ] as const;

    type ExportRow = { [K in typeof headers[number]]: string | number };

    const rows: ExportRow[] = this.tableData.map((row, index) => ({
      'S.No.': (this.pageIndex - 1) * this.rowsPerPage + index + 1,
      'Item No.': row.item_number ?? '',
      'Item Name': row.item_name ?? '',
      'Description': row.description ?? '',
      'Location': row.location ?? '',
      'Salt': row.salt ?? '',
      'Type': row.type ?? '',
      'Unit': row.unit ?? '',
      'Packing Unit': row.packing_unit ?? '',
      'SKU': row.sku ?? '',
      'Manufacturer': row.manufacturer ?? '',
      'Current Stock': row.current_stock ?? 0,
      'Minimum Stock Level': row.minimum_stock ?? 'N/A',
      'Maximum Stock Level': row.maximum_stock ?? 'N/A',
      'Rack No.': row.rack_no ?? 'N/A',
    }));

    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        headers
          .map(header => {
            const cell = row[header] ?? '';
            const escaped = cell.toString().replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(',')
      ),
    ].join('\n');

    // BOM for Excel UTF-8 support
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${this.exportFilename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  exportXlsx() {
    if (!this.tableData.length) {
      this.toasterService.info('Info', 'No data available to export.');
      return;
    }

    const headers = [
      'S.No.', 'Item No.', 'Item Name', 'Description', 'Location', 'Salt', 'Type', 
      'Unit', 'Packing Unit', 'SKU', 'Manufacturer', 'Current Stock', 
      'Minimum Stock Level', 'Maximum Stock Level', 'Rack No.'
    ] as const;

    const data = this.tableData.map((row, index) => ({
      'S.No.': (this.pageIndex - 1) * this.rowsPerPage + index + 1,
      'Item No.': row.item_number ?? '',
      'Item Name': row.item_name ?? '',
      'Description': row.description ?? '',
      'Location': row.location ?? '',
      'Salt': row.salt ?? '',
      'Type': row.type ?? '',
      'Unit': row.unit ?? '',
      'Packing Unit': row.packing_unit ?? '',
      'SKU': row.sku ?? '',
      'Manufacturer': row.manufacturer ?? '',
      'Current Stock': row.current_stock ?? 0,
      'Minimum Stock Level': row.minimum_stock ?? 'N/A',
      'Maximum Stock Level': row.maximum_stock ?? 'N/A',
      'Rack No.': row.rack_no ?? 'N/A',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    worksheet['!cols'] = headers.map(() => ({ wch: 20 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Inventory');
    XLSX.writeFile(workbook, `${this.exportFilename}.xlsx`);
  }

  exportPdf() {
    window.print();
  }

  view(inventoryId: string) {
    // Navigate to inventory detail view if available
    // For now, just show a message
    this.toasterService.info('Info', `Viewing inventory item: ${inventoryId}`);
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(value);
  }
}

